/**
 * Save / restore analysis state across page navigations (auth, billing).
 *
 * Metadata (angles, bike profile) goes in sessionStorage (tiny).
 * Frame image data goes in IndexedDB (50-500 MB+ limit) to avoid the
 * 5 MB sessionStorage quota that PNG base64 frames easily exceed.
 */

import { SimpleFrame, PedalAnalysis } from '../modules/ProcessingPipeline';
import { BikeType, RidingStyle, RidingExperience, Flexibility, PainPoint } from '../constants/bikeConfig';

const STORAGE_KEY = 'bikendure_analysis_state';
const IDB_NAME = 'bikendure_frames';
const IDB_STORE = 'frames';
const IDB_VERSION = 1;

// ---------- types ----------

interface FrameMeta {
  width: number;
  height: number;
  timestamp: number;
  index: number;
  keypoints?: any;
  pedalTag?: 'BDC' | 'TDC' | null;
}

/** Stored in IndexedDB — Blob + metadata per frame. */
interface StoredFrame {
  blob: Blob;
  meta: FrameMeta;
}

/** Stored in sessionStorage — everything except image data. */
interface MetadataSnapshot {
  sessionId: string;
  pedalAnalysis: {
    bdc: PedalAnalysis | null;
    tdc: PedalAnalysis | null;
    trunkAngleAvg: number | null;
    trunkAngleDivergence: boolean;
    pelvicRock?: any;
  };
  bikeType: BikeType;
  ridingStyle: RidingStyle;
  experience: RidingExperience;
  flexibility: Flexibility;
  painPoints: PainPoint[];
  sideFrameMetas: FrameMeta[];
  frontFrameMetas: FrameMeta[];
}

// Re-export for backwards compat (used by AnalysisSnapshot type elsewhere if needed)
export type AnalysisSnapshot = MetadataSnapshot;

// ---------- IndexedDB helpers ----------

function openFrameDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, key: string, value: StoredFrame[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    store.put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<StoredFrame[] | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const store = tx.objectStore(IDB_STORE);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------- frame conversion helpers ----------

function imageDataToBlob(imageData: ImageData): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d')!;
    ctx.putImageData(imageData, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('toBlob returned null'));
      },
      'image/jpeg',
      0.7,
    );
  });
}

async function blobToImageData(blob: Blob, width: number, height: number): Promise<ImageData> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return ctx.getImageData(0, 0, width, height);
}

/** Pick only the BDC/TDC representative frames (max 2 per view). */
function pickRepresentativeFrames(
  frames: SimpleFrame[],
  pedalAnalysis: MetadataSnapshot['pedalAnalysis'],
): SimpleFrame[] {
  const indices = new Set<number>();
  if (pedalAnalysis.bdc) indices.add(pedalAnalysis.bdc.representativeFrameIndex);
  if (pedalAnalysis.tdc) indices.add(pedalAnalysis.tdc.representativeFrameIndex);
  return frames.filter((_, i) => indices.has(i));
}

async function framesToStored(frames: SimpleFrame[]): Promise<StoredFrame[]> {
  return Promise.all(
    frames.map(async (f) => ({
      blob: await imageDataToBlob(f.imageData),
      meta: {
        width: f.imageData.width,
        height: f.imageData.height,
        timestamp: f.timestamp,
        index: f.index,
        keypoints: f.keypoints,
        pedalTag: f.pedalTag,
      },
    })),
  );
}

async function storedToFrames(stored: StoredFrame[]): Promise<SimpleFrame[]> {
  return Promise.all(
    stored.map(async (sf) => ({
      imageData: await blobToImageData(sf.blob, sf.meta.width, sf.meta.height),
      timestamp: sf.meta.timestamp,
      index: sf.meta.index,
      keypoints: sf.meta.keypoints,
      pedalTag: sf.meta.pedalTag,
    } as SimpleFrame)),
  );
}

// ---------- public API ----------

export async function saveAnalysisState(
  sessionId: string,
  sideFrames: SimpleFrame[],
  frontFrames: SimpleFrame[],
  pedalAnalysis: MetadataSnapshot['pedalAnalysis'],
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  experience: RidingExperience,
  flexibility: Flexibility,
  painPoints: PainPoint[],
): Promise<void> {
  const repSide = pickRepresentativeFrames(sideFrames, pedalAnalysis);
  const repFront = pickRepresentativeFrames(frontFrames, pedalAnalysis);

  try {

    // Store frame blobs in IndexedDB
    const db = await openFrameDB();
    const storedSide = await framesToStored(repSide);
    const storedFront = await framesToStored(repFront);
    await idbPut(db, 'side', storedSide);
    await idbPut(db, 'front', storedFront);
    db.close();

    // Store metadata (tiny) in sessionStorage
    const meta: MetadataSnapshot = {
      sessionId,
      pedalAnalysis,
      bikeType,
      ridingStyle,
      experience,
      flexibility,
      painPoints,
      sideFrameMetas: storedSide.map(sf => sf.meta),
      frontFrameMetas: storedFront.map(sf => sf.meta),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
  } catch (e) {
    console.warn('Failed to save analysis state:', e);
  }
}

export async function loadAnalysisState(): Promise<{
  sessionId: string;
  sideFrames: SimpleFrame[];
  frontFrames: SimpleFrame[];
  pedalAnalysis: MetadataSnapshot['pedalAnalysis'];
  bikeType: BikeType;
  ridingStyle: RidingStyle;
  experience: RidingExperience;
  flexibility: Flexibility;
  painPoints: PainPoint[];
} | null> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const meta: MetadataSnapshot = JSON.parse(raw);

    // Read frame blobs from IndexedDB
    const db = await openFrameDB();
    const storedSide = await idbGet(db, 'side');
    const storedFront = await idbGet(db, 'front');

    if (!storedSide) {
      db.close();
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const sideFrames = await storedToFrames(storedSide);
    const frontFrames = storedFront ? await storedToFrames(storedFront) : [];

    // Don't clear storage here — Better Auth's callbackURL can trigger a
    // second full-page reload that wipes React state. The data will be
    // cleared on the next save or via clearSavedAnalysisState().
    db.close();

    // Remap representativeFrameIndex to match restored (smaller) array positions.
    // The stored frames keep their original `.index`, so we find the new position.
    const remapIndex = (frames: SimpleFrame[], origIdx: number): number => {
      const pos = frames.findIndex(f => f.index === origIdx);
      return pos >= 0 ? pos : 0;
    };
    const pa = meta.pedalAnalysis;
    if (pa.bdc) pa.bdc.representativeFrameIndex = remapIndex(sideFrames, pa.bdc.representativeFrameIndex);
    if (pa.tdc) pa.tdc.representativeFrameIndex = remapIndex(sideFrames, pa.tdc.representativeFrameIndex);

    return {
      sessionId: meta.sessionId,
      sideFrames,
      frontFrames,
      pedalAnalysis: pa,
      bikeType: meta.bikeType,
      ridingStyle: meta.ridingStyle,
      experience: meta.experience,
      flexibility: meta.flexibility,
      painPoints: meta.painPoints,
    };
  } catch (e) {
    console.warn('Failed to load analysis state:', e);
    return null;
  }
}

export async function clearSavedAnalysisState(): Promise<void> {
  sessionStorage.removeItem(STORAGE_KEY);
  try {
    const db = await openFrameDB();
    await idbClear(db);
    db.close();
  } catch { /* best effort */ }
}

export function hasSavedAnalysisState(): boolean {
  return sessionStorage.getItem(STORAGE_KEY) !== null;
}
